<template>
  <v-col>
    <v-slider
      thumb-label="always"
      :thumb-size="thumbSize"
      :label="$t('announce.columns.speed')"
      v-model="ttsSpeed"
      step="0.01"
      max="2.0"
      min="0.35"
      class="pr-6 mt-8"
    ></v-slider>
    <v-slider
      thumb-label="always"
      :thumb-size="thumbSize"
      :label="$t('announce.columns.pitch')"
      v-model="ttsPitch"
      max="20"
      min="-20"
      class="pr-6 pt-4"
    ></v-slider>
    <v-slider
      thumb-label="always"
      :thumb-size="thumbSize"
      :label="$t('announce.columns.volumeGainDb')"
      v-model="ttsVolumeGainDb"
      max="16"
      min="-96"
      class="pr-6 pt-4"
    ></v-slider>
    <v-autocomplete
      v-model="ttsVoiceLanguage"
      :items="voiceLanguages"
      item-text="languageCode"
      item-value="languageCode"
      :label="$t('announce.columns.voiceLanguage')"
      :rules="[isRequired]"
      class="pr-6"
    ></v-autocomplete>
    <v-autocomplete
      v-model="ttsVoiceCategory"
      :items="ttsVoiceCategories"
      item-text="title"
      item-value="key"
      :label="$t('announce.columns.voiceCategory')"
      :rules="[isRequired]"
      class="pr-6"
    ></v-autocomplete>
    <v-autocomplete
      v-model="ttsVoiceType"
      :items="voices"
      item-text="name"
      item-value="name"
      :label="$t('announce.columns.voiceType')"
      :rules="[isRequired]"
      class="pr-6">
      <template slot="selection" slot-scope="data">
        {{ data.item.name }} - {{ data.item.ssmlGender }}
      </template>
      <template slot="item" slot-scope="data">
        {{ data.item.name }} - {{ data.item.ssmlGender }}
      </template>
    </v-autocomplete>
    <v-select
      v-model="ttsTextType"
      :items="textTypes"
      item-text="title"
      item-value="key"
      :label="$t('announce.columns.textType')"
      class="pr-6"
    ></v-select>
    <v-textarea v-model="tts" rows="3"
      :label="$t('announce.edit.t2s')"
      :counter="ttsMaxChars"
      :maxlength="ttsMaxChars"
      :rules="[isRequired, ttsMaxLengthRule]"
      class="pr-6">
    </v-textarea>
    <v-btn color="primary" @click="preview()" :loading="loading" :disabled="!canPreview">
      <v-icon left>
        mdi-clipboard-text-play
      </v-icon>
      {{ $t('announce.tts.buildBtn') }}
    </v-btn>
    <av-waveform v-if="previewSrcURL"
      class="mt-4"
      :canv-width="300"
      :canv-height="80"
      :played-line-color="graphColor"
      :noplayed-line-color="graphColor"
      :playtime-slider-color="graphColor"
      :playtime-font-color="graphColor"
      :audio-src="previewSrcURL">
    </av-waveform>
  </v-col>
</template>

<script>
import { mapGetters } from 'vuex'
import API from '@/api'
import events from 'Store/events'

export default {
  data () {
    return {
      ttsMaxChars: 2000,
      thumbSize: 33,
      graphColor: '#2196f3',
      previewSrcURL: null,
      loading: false,
      voicesIncompatibleWithSSML: [
        'Chirp-HD',
        'Chirp3-HD'
      ],
      ttsTextTypes: [
        { key: 'text', title: this.$t('announce.columnRenderer.channelTypes.text') },
        { key: 'ssml', title: this.$t('announce.columnRenderer.channelTypes.ssml') }
      ],
      ttsVoiceCategories: [
        { key: 'Standard', title: this.$t('announce.columnRenderer.voiceCategories.standard') },
        { key: 'News', title: this.$t('announce.columnRenderer.voiceCategories.news') },
        { key: 'Wavenet', title: this.$t('announce.columnRenderer.voiceCategories.wavenet') },
        { key: 'Polyglot', title: this.$t('announce.columnRenderer.voiceCategories.polyglot') },
        { key: 'Chirp-HD', title: this.$t('announce.columnRenderer.voiceCategories.chirpHD') },
        { key: 'Chirp3-HD', title: this.$t('announce.columnRenderer.voiceCategories.chirp3HD') },
        { key: 'Studio', title: this.$t('announce.columnRenderer.voiceCategories.studio') },
        { key: 'Neural2', title: this.$t('announce.columnRenderer.voiceCategories.neural2') }
      ],
      rules: {
        required: v => !!v || this.$t('announce.edit.requiredForTTS'),
        numeric: v => /^\d+$/.test(v) || this.$t('announce.edit.numeric')
      },
      ttsVoiceLanguage: null,
      ttsVoiceCategory: null
    }
  },
  created () {
    this.ttsVoiceLanguage = this.voiceType?.split('-').slice(0, 2).join('-') ?? null
    this.ttsVoiceCategory = this.ttsVoiceCategories.find((category) =>
      this.voiceType?.includes(category?.key)
    )?.key ?? null
  },
  props: {
    textToSpeech: {
      type: String,
      default: ''
    },
    speed: {
      type: Number,
      default: 1
    },
    pitch: {
      type: Number,
      default: 0
    },
    volumeGainDb: {
      type: Number,
      default: 0
    },
    textType: {
      type: String,
      default: 'text'
    },
    voiceType: {
      type: String,
      default: ''
    }
  },
  computed: {
    ...mapGetters({
      voiceTypes: 'announcements/voiceTypes'
    }),
    isRequired () {
      return this.rules?.required
    },
    voiceLanguages () {
      return this.voiceTypes.map(voiceType => {
        return { languageCode: voiceType.languageCodes[0] }
      })
    },
    voices () {
      if (this.ttsVoiceCategory && this.ttsVoiceLanguage) {
        return this.voiceTypes.filter(voiceType => {
          return voiceType.name.match(this.ttsVoiceCategory) && voiceType.languageCodes[0] === this.ttsVoiceLanguage
        })
      } else {
        return this.voiceTypes
      }
    },
    textTypes () {
      if (this.voicesIncompatibleWithSSML.includes(this.ttsVoiceCategory)) {
        return this.ttsTextTypes.filter(textType => {
          return textType.key.match('text')
        })
      } else {
        return this.ttsTextTypes
      }
    },
    ttsApiParams () {
      return {
        speed: this.ttsSpeed,
        pitch: this.ttsPitch,
        volume_gain_db: this.ttsVolumeGainDb,
        voice_type: this.ttsVoiceType,
        text_type: this.ttsTextType,
        text_to_speech: this.tts,
        language: `${this.ttsVoiceType?.split('-')[0]}-${this.ttsVoiceType?.split('-')[1].split('-')[0]}`
      }
    },
    ttsSpeed: {
      get: function () { return this.speed },
      set: function (value) { this.$emit('update-speed', value) }
    },
    ttsPitch: {
      get: function () { return this.pitch },
      set: function (value) { this.$emit('update-pitch', value) }
    },
    ttsVolumeGainDb: {
      get: function () { return this.volumeGainDb },
      set: function (value) { this.$emit('update-volume-gain-db', value) }
    },
    tts: {
      get: function () { return this.textToSpeech },
      set: function (value) { this.$emit('update-text-to-speech', value) }
    },
    ttsTextType: {
      get: function () { return this.textType },
      set: function (value) { this.$emit('update-text-type', value) }
    },
    ttsVoiceType: {
      get: function () { return this.voiceType },
      set: function (value) { this.$emit('update-voice-type', value) }
    },
    canPreview () {
      return this.ttsSpeed &&
        this.ttsPitch >= -20 &&
        this.ttsVolumeGainDb >= -96 &&
        this.tts &&
        this.tts.length <= this.ttsMaxChars &&
        this.ttsTextType &&
        this.ttsVoiceType
    },
    ttsMaxLengthRule () {
      return (v) => !v || v.length <= this.ttsMaxChars || this.$t('announce.edit.ttsMaxLength', { max: this.ttsMaxChars })
    }
  },
  methods: {
    preview () {
      if (this.tts && this.tts.length > this.ttsMaxChars) {
        events.pubError(this.$t('announce.edit.ttsMaxLength', { max: this.ttsMaxChars }))
        return
      }
      this.loading = true
      this.previewSrcURL = null
      API.previewTTSAnnouncementStream(this.ttsApiParams).then(({ data, status }) => {
        if (!data || status !== 200) {
          throw new Error('Failed to generate text to speech')
        }
        this.dbg('Generated text to speech')
        this.previewSrcURL = URL.createObjectURL(new Blob([data], { type: 'audio/mpeg' }))
      }).catch(async err => {
        let apiResponse = null
        // Handle the case where err.response.data is a blob
        if (err.response && err.response.data instanceof Blob) {
          const text = await new Response(err.response.data).text()
          try {
            apiResponse = JSON.parse(text)
          } catch (jsonError) {
            // Handle the case where the blob is not JSON
            apiResponse = text
          }
        }
        this.dbg('Failed to generate text to speech')
        this.dbg(err)
        events.pubError(apiResponse?.exception)
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style>

</style>
